import { Injectable } from '@angular/core'
import { NavigationStart, Router } from '@angular/router'
import { BehaviorSubject, filter } from 'rxjs'

/**
 * States a screen can be put into on demand while branding it.
 *
 * Some of what needs styling only shows up after something goes wrong, and in preview mode
 * there is no way to make it go wrong: the services are mocked and the login always succeeds.
 * Forcing the state is what makes those messages reachable at all.
 *
 * A screen offers its own states rather than a central list naming them, for two reasons: the
 * editor can only ever show what the screen in front of you actually implements, so choosing
 * one can no longer do nothing; and a screen gaining a state is a change in that screen alone.
 */
export interface PreviewStateOffer {
    /** Stable key. Also the translation key under themeConfiguration.states. */
    key: string
}

@Injectable({ providedIn: 'root' })
export class PreviewStateService {

    /** What the screen in front of you can demonstrate. Empty means it has no failure states. */
    readonly offered = new BehaviorSubject<PreviewStateOffer[]>([])

    /** More than one can be on: a screen may fail in several ways at once. */
    readonly active = new BehaviorSubject<ReadonlySet<string>>(new Set())

    constructor(private router: Router) {
        // Cleared as the navigation starts, not as it ends: a screen offers its states from its
        // enter hook, which runs before the navigation finishes, so clearing at the end would
        // wipe what the screen being opened had just offered.
        this.router.events
            .pipe(filter(event => event instanceof NavigationStart))
            .subscribe(() => this.clear())
    }

    /**
     * Called while on screen by whatever can demonstrate the state: the page itself, or a shared
     * control such as the validation messages, which brings its own state to every screen that
     * uses it. Adds rather than replaces, and navigating away is what empties the list, so it
     * can never name a state that is not on the screen in front of you.
     */
    offer(states: PreviewStateOffer[]): void {
        const known = new Set(this.offered.value.map(o => o.key))
        const added = states.filter(o => !known.has(o.key))

        if (added.length) this.offered.next([...this.offered.value, ...added])
    }

    is(key: string): boolean {
        return this.active.value.has(key)
    }

    toggle(key: string): void {
        const next = new Set(this.active.value)

        if (!next.delete(key)) next.add(key)

        this.active.next(next)
    }

    clear(): void {
        this.offered.next([])
        this.active.next(new Set())
    }
}
